import type {
  ProviderKind,
  ProviderUnavailableDetail,
  ProviderUnavailableReason,
} from "@cmmd-forge/contracts";

/**
 * The vocabulary `providerAvailabilityJoin.ts` speaks: the picker rail's rows,
 * the roadmap gate, and the shape of one availability answer. Split out of
 * that file so it stays under the file-size ratchet's cap; the join's own
 * eligibility and decoding logic lives there, this is what it and its callers
 * name things with.
 */

/**
 * A row on the picker rail.
 *
 * Wider than `ProviderKind` because a roadmap row is a marketing promise, not a
 * provider the build can address: Gemini has no `ProviderKind`, no adapter and
 * no snapshot, and giving it one to draw a rail button would put an
 * unroutable provider into every model selection type in the app.
 */
export type PickerRailProvider = ProviderKind | "gemini";

/**
 * The providers the PRODUCT has not shipped, published as Unbuilt.
 *
 * Deliberately not the same list as the server's `UNBUILT_PROVIDERS` in
 * `apps/server/src/provider/providerAvailability.ts`, and the difference is the
 * point. The server's list names what the BUILD cannot execute: it is empty,
 * because cursor and opencode both have a detection Layer and a registered
 * adapter, so an Environment publishes them with a real probe result. This list
 * names what the PRODUCT has not shipped to a customer. A provider is Unbuilt
 * when either list says so, and this one wins, because "Your organization has
 * not enabled Cursor." would send an admin to a policy switch that does not
 * exist.
 *
 * Removing a row from here is the deliberate act that ships a provider. That is
 * the whole reason the roadmap is a list rather than a fallback: "Coming soon."
 * is now something a person chose to say, never something a provider fell into.
 */
export const UNBUILT_PICKER_PROVIDERS: ReadonlyArray<PickerRailProvider> = [
  "cursor",
  "opencode",
  "gemini",
];

/**
 * A withheld detail only the Shell can produce, never one an Environment puts
 * on the wire.
 *
 * `environment-not-running` is a Not Usable sub-case: CMMD's readiness answer
 * (`reason: "runtime_unavailable"`, CMMD-36473) says the Environment itself
 * cannot take a delivery, which an Environment can never report about itself.
 *
 * `could-not-check` pairs with Not on this Environment instead: a reading this
 * join cannot trust as confirmed, either because the live snapshot's most
 * recent probe failed, timed out, or errored (`ServerProvider.availabilityCheck`),
 * or because there is no live connection at all and the reading came from
 * CMMD's last-known catalog, which never carries a confirmed answer either
 * (forge#3588).
 *
 * Both are kept off the contract's `ProviderUnavailableDetail` for the same
 * reason: that union is also the inventory wire CMMD's receiver validates,
 * and neither fact is one an Environment publishes.
 */
export type ShellOnlyProviderUnavailableDetail = "environment-not-running" | "could-not-check";
export type ShellProviderUnavailableDetail =
  | ProviderUnavailableDetail
  | ShellOnlyProviderUnavailableDetail;

/**
 * The subset of `ShellProviderUnavailableDetail` that pairs with reason
 * `not-usable`. Its own type because `could-not-check` pairs with
 * `not-on-this-environment` instead, and a `Record` keyed by the wider union
 * would force a dead entry into every Not Usable copy table.
 */
export type NotUsableShellDetail = Exclude<ShellProviderUnavailableDetail, "could-not-check">;

export type ShellProviderAvailability =
  | { readonly state: "usable" }
  | {
      readonly state: "withheld";
      readonly reason: ProviderUnavailableReason;
      readonly detail?: ShellProviderUnavailableDetail;
    }
  /**
   * Not a fifth reason. CMMD has not answered yet, or answering failed, so the
   * Shell has no verdict to report and must not invent one.
   */
  | { readonly state: "unresolved"; readonly cause: "loading" | "error" };

export const USABLE: ShellProviderAvailability = { state: "usable" };
export const UNBUILT: ShellProviderAvailability = { state: "withheld", reason: "unbuilt" };
export const NOT_ON_THIS_ENVIRONMENT: ShellProviderAvailability = {
  state: "withheld",
  reason: "not-on-this-environment",
};

export function isUnbuiltPickerProvider(provider: PickerRailProvider): boolean {
  return UNBUILT_PICKER_PROVIDERS.includes(provider);
}

/**
 * The `ProviderKind` behind a rail row, or `null` for a roadmap-only row.
 *
 * A row with no kind can only ever be Unbuilt: nothing publishes it, no policy
 * key names it, and no turn can be dispatched to it.
 */
export function providerKindForRail(provider: PickerRailProvider): ProviderKind | null {
  return provider === "gemini" ? null : provider;
}
